import React, { useEffect, useState } from 'react';
import { StudyPlanEntity } from 'Models/Entities';
import Modal from 'Views/Components/Modal/Modal';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { DisplayType } from 'Views/Components/Models/Enums';
import { Button, Display } from 'Views/Components/Button/Button';
import { getStudyPlansByName } from 'Queries/GraphQlQueries/StudyPlan/StudyPlanQueries';
import alertToast from 'Util/ToastifyUtils';
import { observer } from 'mobx-react';

interface IStudyPlanDuplicateModalProps {
	isOpen: boolean,
	closeModal: () => void;
	onConfirm: (studyPlanId:string, name: string) => Promise<void>,
	initialPlan: StudyPlanEntity,
	existingStudyPlanNames?: string[];
}

const StudyPlanDuplicateModal = observer((
	{
		isOpen, closeModal, onConfirm, initialPlan, existingStudyPlanNames,
	}: IStudyPlanDuplicateModalProps,
) => {
	const [
		studyPlan, setStudyPlan,
	] = useState<StudyPlanEntity>(initialPlan);
	const [planName, setPlanName] = useState<string>(initialPlan.name);
	const [error, setError] = useState<string>();

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const nameSuggestion = async () => {
			const { studyPlanEntitys: existingPlansWithSameName } = await getStudyPlansByName(initialPlan.name);
			return initialPlan.name + (existingPlansWithSameName.length > 0
				? ` ${existingPlansWithSameName.length}`
				: '');
		};

		nameSuggestion().then(res => {
			setPlanName(initialPlan.name);
			setStudyPlan(new StudyPlanEntity({
				...initialPlan,
				name: res,
			}));
		});
	}, [initialPlan, isOpen]);

	const clearModal = () => {
		setStudyPlan(new StudyPlanEntity());
		setPlanName('');
		setError(undefined);
	};

	const onCancel = () => {
		clearModal();
		closeModal();
	};

	const onConfirmBtnClick = async () => {
		if (!studyPlan.name || studyPlan.name?.length <= 0) {
			setError('Please provide a name for your study plan');
			return;
		}

		if (existingStudyPlanNames?.includes(studyPlan.name)) {
			setError('Study plan name already exists');
			return;
		}

		await onConfirm(studyPlan.id, studyPlan.name).then(res => {
			clearModal();
		}).catch(err => {
			alertToast('Cannot duplicate study plan!', 'error');
		});
	};

	return (
		<Modal
			isOpen={isOpen}
			label="Duplicate study plan"
			onRequestClose={onCancel}
		>
			<h3>Duplicate study plan</h3>
			<p>
				Are you sure you want to duplicate <strong>`{planName}`</strong> study plan?
			</p>
			<p>
				You can rename the duplicate below, or rename after the plan later.
			</p>
			<TextField
				model={studyPlan}
				modelProperty="name"
				id="study-plan-duplicate-name"
				name="study-plan-duplicate-name"
				displayType={DisplayType.BLOCK}
				label="Name"
				labelVisible
				placeholder="Enter study plan name"
				onAfterChange={evt => {
					setError(undefined);
					setStudyPlan(prevState => {
						return new StudyPlanEntity({
							...prevState,
							name: evt.target.value,
						});
					});
				}}
				isRequired
				errors={error}
			/>
			<div key="actions" className="modal__actions">
				<Button
					key="cancel"
					className="modal--cancel"
					onClick={onCancel}
					display={Display.Text}
				>
					Cancel
				</Button>
				<Button
					key="confirm"
					className="modal--confirm"
					testId="next-btn"
					onClick={onConfirmBtnClick}
					display={Display.Solid}
				>
					Yes, duplicate
				</Button>
			</div>
		</Modal>
	);
});

export default StudyPlanDuplicateModal;
